import { useCallback, useRef, useState } from 'react';
import {
  Flex,
  InputBox,
  PrimaryButton,
  StackScreen,
  usePopup,
  useScreenSize,
} from '@banhala/ui-kit';
import { ApiError } from 'shared/src/api/httpClient';
import { CartIcon } from 'shared/src/component';
import { AnalyticsScreen } from 'shared/src/context';
import { useHandleBackPressed } from 'shared/src/hooks';
import { bridge, isWeb } from 'shared/src/utils';
import { useValidatedInput } from '../EmailLogin/hooks/useValidatedInput';
import { Countdown } from '../../components/Countdown';
import { useLogClickConfirmVerification } from '../../hooks/useLogClickConfirmVerification';
import { useLogClickRequestVerification } from '../../hooks/useLogClickRequestVerification';
import { useResetPasswordModal } from './components/useResetPasswordModal';
import { useLogViewFindPassword } from './hooks/useLogViewFindPassword';
import { useLogViewFoundPassword } from './hooks/useLogViewFoundPassword';
import { usePostRecoverPassword } from './queries/usePostRecoverPassword';
import { usePostValidatePasswordAuth } from './queries/usePostValidatePasswordAuth';
import { validateEmail } from './validation';

const PHONE_REGEX = /^01\d{8,9}$/;

const onlyDigits = (value: string) => value.replace(/[^0-9]/g, '');

const RecoveryPasswordContent = () => {
  const { width } = useScreenSize();
  const { showBackButton, onPressBack } = useHandleBackPressed();
  const { show: showPopup } = usePopup();
  const { show: showResetPassword } = useResetPasswordModal();
  const { mutateAsync: recoverPassword } = usePostRecoverPassword();
  const { mutateAsync: validatePasswordAuth } = usePostValidatePasswordAuth();

  const emailField = useValidatedInput(validateEmail);

  const logClickRequestVerification = useLogClickRequestVerification();
  const logClickConfirmVerification = useLogClickConfirmVerification();
  const logViewFoundPassword = useLogViewFoundPassword();

  useLogViewFindPassword();

  const [phone, setPhone] = useState('');
  const [authCode, setAuthCode] = useState('');
  const [publicCode, setPublicCode] = useState('');
  const [deadline, setDeadline] = useState(0);

  // 발급/검증 in-flight 중복 실행(더블탭) 차단.
  const busyRef = useRef(false);

  // 인증번호가 발급되면 검증 단계
  const waiting = publicCode !== '';

  const isPhoneValid = PHONE_REGEX.test(phone);
  const canIssue = emailField.isValid && isPhoneValid;

  const resetAuthState = useCallback(() => {
    setPublicCode('');
    setAuthCode('');
    setDeadline(0);
  }, []);

  const handleExpire = useCallback(() => {
    setPublicCode('');
    setDeadline(0);
  }, []);

  const handleAuthCodeChange = useCallback((value: string) => {
    setAuthCode(onlyDigits(value));
  }, []);

  const handleEmailChange = useCallback(
    (value: string) => {
      emailField.onChangeText(value);
      if (waiting) resetAuthState();
    },
    [emailField, resetAuthState, waiting],
  );

  const handlePhoneChange = useCallback(
    (value: string) => {
      const nextPhone = onlyDigits(value);
      if (nextPhone === phone) return;
      setPhone(nextPhone);
      if (waiting) resetAuthState();
    },
    [phone, resetAuthState, waiting],
  );

  const handleIssue = useCallback(async () => {
    if (busyRef.current || !canIssue) return;
    logClickRequestVerification();
    busyRef.current = true;
    try {
      const { public_code, remain_seconds } = await recoverPassword({
        email: emailField.value,
        mobile: phone,
      });
      setPublicCode(public_code);
      setDeadline(Date.now() + remain_seconds * 1000);
      setAuthCode('');
    } catch (error) {
      bridge.toast(
        error instanceof ApiError && error.message
          ? error.message
          : '인증번호 발급에 실패했어요',
      );
    } finally {
      busyRef.current = false;
    }
  }, [canIssue, emailField, logClickRequestVerification, phone, recoverPassword]);

  const handleVerify = useCallback(async () => {
    if (busyRef.current || !waiting || authCode === '') return;
    logClickConfirmVerification();
    // 만료된 코드로의 검증 방어
    if (Date.now() >= deadline) return;
    busyRef.current = true;
    try {
      const { access_code } = await validatePasswordAuth({
        public_code: publicCode,
        auth_code: authCode,
      });
      setDeadline(0);
      logViewFoundPassword();
      const result = await showResetPassword({ accessCode: access_code });
      if ('data' in result && result.data.expired) {
        // access_code 만료 안내 후 화면을 리셋해 처음부터 다시 시도하도록 유도(webview 정합).
        await showPopup({
          title: '요청 시간이 만료되었어요',
          message: '다시 시도해 주세요.',
          confirm: '확인',
        });
        if (isWeb) {
          window.location.reload();
        }
        return;
      }
      await showPopup({
        title: '비밀번호를 변경했어요',
        message: '새로운 비밀번호로 로그인해 주세요.',
        confirm: '로그인하러 가기',
      });
      onPressBack();
    } catch (error) {
      bridge.toast(
        error instanceof ApiError && error.message
          ? error.message
          : '인증에 실패했어요',
      );
    } finally {
      busyRef.current = false;
    }
  }, [
    authCode,
    deadline,
    publicCode,
    validatePasswordAuth,
    waiting,
    logClickConfirmVerification,
    logViewFoundPassword,
    showResetPassword,
    showPopup,
    onPressBack,
  ]);

  return (
    <StackScreen
      title={'비밀번호 찾기'}
      hideBackButton={!showBackButton}
      onPressBack={onPressBack}
      rightIcons={[<CartIcon key={'cart'} />]}
    >
      <Flex flex={1} width={width} alignSelf={'center'}>
        <Flex padding={{ horizontal: 16, top: 16 }} gap={16}>
          <InputBox
            type={'underline'}
            placeholder={'이메일 입력'}
            value={emailField.value}
            onChangeText={handleEmailChange}
            keyboardType={'email-address'}
            autoCapitalize={'none'}
            helperText={emailField.helperText}
          />

          <Flex gap={8}>
            <Flex horizontal alignItems={'center'} gap={8}>
              <Flex flex={1}>
                <InputBox
                  type={'underline'}
                  placeholder={'휴대폰 번호 (숫자만 입력)'}
                  value={phone}
                  onChangeText={handlePhoneChange}
                  keyboardType={'number-pad'}
                />
              </Flex>
              <PrimaryButton
                variant={'hug'}
                outline
                color={'gray'}
                size={'medium'}
                state={!canIssue ? 'disabled' : undefined}
                onPress={handleIssue}
              >
                {waiting ? '다시 받기' : '인증번호'}
              </PrimaryButton>
            </Flex>

            {waiting && (
              <Flex gap={8}>
                <InputBox
                  type={'underline'}
                  placeholder={'인증번호 입력'}
                  value={authCode}
                  onChangeText={handleAuthCodeChange}
                  keyboardType={'number-pad'}
                />
                <Countdown deadline={deadline} onExpire={handleExpire} />
                <PrimaryButton
                  variant={'fill'}
                  color={'gray'}
                  size={'large'}
                  state={authCode === '' ? 'disabled' : undefined}
                  onPress={handleVerify}
                >
                  {'확인'}
                </PrimaryButton>
              </Flex>
            )}
          </Flex>
        </Flex>
      </Flex>
    </StackScreen>
  );
};

export const RecoveryPasswordScreen = () => (
  <AnalyticsScreen screen={'FIND_PASSWORD'} hostsModals={false}>
    <RecoveryPasswordContent />
  </AnalyticsScreen>
);
